import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, faTrash, faGripVertical, faSave, 
    faFileAlt, faLayerGroup, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import Spinner from '../../shared/spinner';
import LayoutRelatorioService from '../../services/layoutRelatorioService'; 
import { useSlug } from '../hooks/UseSlug';

function ConfiguradorLayoutRelatorio() {
    const layoutService = useMemo(() => new LayoutRelatorioService(), []);
    const location = useLocation();
    const navigate = useNavigate();
    const slug = useSlug();

    // Estados principais
    const [idLayout, setIdLayout] = useState<number | null>(null); // Guardar o ID para edição
    const [relatorioBase, setRelatorioBase] = useState('PRODUCAO_ANALITICO');
    const [tituloArquivo, setTituloArquivo] = useState('');
    const [colunasAtivas, setColunasAtivas] = useState<any[]>([]);
    const [camposDisponiveis, setCamposDisponiveis] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // 1. Efeito para capturar dados vindos da Listagem (Navegação com State)
    useEffect(() => {
        if (location.state && location.state.layout) {
            const layoutParaEditar = location.state.layout;
            setIdLayout(layoutParaEditar.id);
            setRelatorioBase(layoutParaEditar.nome);
            setTituloArquivo(layoutParaEditar.tituloArquivo || '');
            
            // Verifica se o layoutJson é string ou objeto
            const colunas = typeof layoutParaEditar.layoutJson === 'string' 
                ? JSON.parse(layoutParaEditar.layoutJson) 
                : layoutParaEditar.layoutJson;
                
            setColunasAtivas(colunas);
        }
    }, [location.state]);

    // 2. Carrega os campos técnicos disponíveis
    useEffect(() => {
        const carregarCamposTecnicos = async () => {
            try {
                setLoading(true);
                const res = await layoutService.listarCamposDisponiveis(relatorioBase);
                setCamposDisponiveis(res.data || []);
            } catch (error) {
                console.error("Erro ao carregar campos técnicos", error);
            } finally {
                setLoading(false);
            }
        };
        carregarCamposTecnicos();
    }, [relatorioBase, layoutService]);

    // 3. Salvar Layout (Criação ou Alteração)
    const salvarLayout = async () => {
        if (colunasAtivas.length === 0 || !tituloArquivo) {
            alert("Adicione colunas e defina o nome do arquivo.");
            return;
        }

        try {
            setLoading(true);
            const payload = {
                id: idLayout, // Se for nulo, o Spring Boot cria um novo. Se tiver ID, ele atualiza.
                nome: relatorioBase,
                tituloArquivo: tituloArquivo,
                layoutJson: JSON.stringify(colunasAtivas)
            };

            await layoutService.salvarLayout(payload);
            alert('Layout salvo com sucesso!');
            
            // Após salvar, volta para a listagem
            navigate(`${slug ? `/${slug}` : ''}/relatorios-layout`);
        } catch (error) {
            alert('Erro ao salvar layout.');
        } finally {
            setLoading(false);
        }
    };

    const adicionarCampo = (campo: any) => {
        if (colunasAtivas.find(c => c.prop === campo.prop)) return;
        setColunasAtivas([...colunasAtivas, { ...campo }]);
    };

    const alterarLabel = (index: number, novoLabel: string) => {
        const novasColunas = [...colunasAtivas];
        novasColunas[index].label = novoLabel;
        setColunasAtivas(novasColunas);
    };

    const removerCampo = (prop: string) => {
        setColunasAtivas(colunasAtivas.filter(c => c.prop !== prop));
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(colunasAtivas);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setColunasAtivas(items);
    };

    return (
        <div className="container-fluid mt-4 px-md-5">
            {loading && <Spinner loading={true} />}

            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center">
                    {/* <button 
                        className="btn btn-link text-dark me-3" 
                        onClick={() => navigate(-1)}
                        title="Voltar para a lista"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} size="lg" />
                    </button> */}
                    <div>
                        <h3 className="fw-bold mb-0">
                            {idLayout ? `Editando Layout #${idLayout}` : 'Novo Layout de Relatório'}
                        </h3>
                        <small className="text-muted fw-bold text-uppercase">Arraste para ordenar e edite os nomes das colunas</small>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-dark fw-bold px-4 shadow" onClick={salvarLayout}>
                        <FontAwesomeIcon icon={faSave} className="me-2" /> SALVAR LAYOUT
                    </button>
                </div>
            </div>

            <div className="row">
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header bg-light py-3 fw-bold small text-uppercase">Campos Disponíveis</div>
                        <div className="list-group list-group-flush" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {camposDisponiveis.map(campo => (
                                <button 
                                    key={campo.prop}
                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                                    onClick={() => adicionarCampo(campo)}
                                    disabled={colunasAtivas.some(c => c.prop === campo.prop)}
                                >
                                    <span className="fw-bold small">{campo.label}</span>
                                    <FontAwesomeIcon icon={faPlus} className="text-primary" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-md-8">
                    <div className="card shadow-sm border-0 p-4 mb-4 bg-light">
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="text-muted small fw-bold mb-2 text-uppercase">Relatório Base:</label>
                                <select 
                                    className="form-select fw-bold shadow-sm" 
                                    value={relatorioBase} 
                                    onChange={e => setRelatorioBase(e.target.value)}
                                    disabled={idLayout !== null} 
                                >
                                    <option value="PRODUCAO_ANALITICO">RELATÓRIO PRODUÇÃO ANALÍTICO</option>
                                </select>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted small fw-bold mb-2 text-uppercase">Nome do Layout:</label>
                                <div className="input-group shadow-sm">
                                    <span className="input-group-text bg-white border-end-0 text-muted">
                                        <FontAwesomeIcon icon={faFileAlt} />
                                    </span>
                                    <input 
                                        type="text" 
                                        className="form-control fw-bold border-start-0" 
                                        value={tituloArquivo} 
                                        onChange={e => setTituloArquivo(e.target.value)} 
                                        placeholder="Ex: Vendas Consignado Janeiro" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-dark text-white py-3 fw-bold small text-uppercase">Estrutura de Colunas</div>
                        <div className="card-body p-3 bg-white">
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="colunas">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                            {colunasAtivas.map((coluna, index) => (
                                                <Draggable key={coluna.prop} draggableId={coluna.prop} index={index}>
                                                    {(provided) => (
                                                        <div 
                                                            ref={provided.innerRef} 
                                                            {...provided.draggableProps} 
                                                            className="card border mb-2 shadow-sm" 
                                                            style={{...provided.draggableProps.style, borderLeft: '5px solid #0d6efd'}}
                                                        >
                                                            <div className="card-body d-flex align-items-center py-2 px-3">
                                                                <div {...provided.dragHandleProps} className="me-3 text-muted">
                                                                    <FontAwesomeIcon icon={faGripVertical} />
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <small className="text-muted fw-bold d-block" style={{ fontSize: '0.65rem' }}>NOME DA COLUNA NO EXCEL:</small>
                                                                    <input 
                                                                        type="text" 
                                                                        className="form-control form-control-sm fw-bold border-0 p-0" 
                                                                        value={coluna.label} 
                                                                        onChange={e => alterarLabel(index, e.target.value)}
                                                                        style={{ backgroundColor: 'transparent', boxShadow: 'none' }}
                                                                    />
                                                                    <small className="text-primary" style={{fontSize: '0.6rem'}}>Campo Original: {coluna.prop}</small>
                                                                </div>
                                                                <button 
                                                                    className="btn btn-link text-danger p-0 ms-2" 
                                                                    onClick={() => removerCampo(coluna.prop)}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            {colunasAtivas.length === 0 && (
                                                <div className="text-center py-5 text-muted small">
                                                    Nenhuma coluna selecionada. Adicione campos do lado esquerdo.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfiguradorLayoutRelatorio;